import { gql } from 'graphql-request';

export const MetaFragment = gql`
  fragment Meta on Meta {
    name
    schema
    deliveryKey
    deliveryId
    url
  }
`;

export const LinkFragment = gql`
  fragment Link on Link {
    link
    externalLink
    isExternal
    displayName
    asButton
    anchorTag
  }
`;

export const AssetFragment = gql`
  fragment Asset on Assets {
    accessibilityLabel
    name
    url
    defaultHost
    endpoint
  }
`;

export const VideoFragment = gql`
  fragment Video on Video {
    contentUrl
    duration
    embedUrl
    externalVideoId
    name
    source
    thumbnailUrl
    uploadDate
    uploadedThumbnail {
      ...Asset
    }
    videoAsset {
      defaultHost
      endpoint
      id
      name
    }
  }
`;

export const MeasureFragment = gql`
  fragment Measure on Measure {
    measurement
    measurementDisplay
    unitOfMeasure
    weight
  }
`;

export const ShopableListFragment = gql`
  fragment ShopableList on ShopableList {
    _meta {
      ...Meta
    }
    icon {
      ...Asset
    }
    title
    skus
    description
  }
`;

export const SeoFragment = gql`
  fragment Seo on SEO {
    noIndex
    noFollow
    ogTitle
    ogDescription
    ogType
    twitterCard
    twitterCreator
    pageType
    ogImage {
      ...Asset
    }
    _meta {
      ...Meta
    }
  }
`;

export const BreadCrumbFragment = gql`
  fragment BreadCrumb on BreadCrumb {
    _meta {
      ...Meta
    }
    breadcrumb {
      displayName
      isExternal
      noFollow
      noIndex
      link
      externalLink
    }
    dark
    showSocial
  }
`;

export const ImageFragment = gql`
  fragment Image on Image {
    _meta {
      schema
    }
    altTag
    image {
      ...Asset
    }
    static
  }
`;

export const LinkWithIconFragment = gql`
  fragment LinkWithIcon on LinkWithIcon {
    _meta {
      ...Meta
    }
    displayName
    externalLink
    isExternal
    link
    noFollow
    noIndex
    anchorTag
    imageContent {
      ...Image
    }
  }
  ${ImageFragment}
`;

export const LinkGroupFragment = gql`
  fragment LinkGroup on LinkGroup {
    _meta {
      ...Meta
    }
    title
    links {
      ...LinkWithIcon
    }
  }
`;

export const LinkListFragment = gql`
  fragment LinkList on LinkList {
    title
    columns {
      ...LinkGroup
    }
    inline
    _meta {
      ...Meta
    }
  }
`;

export const NavProductCardFragment = gql`
  fragment NavProductCard on NavProductCard {
    _meta {
      ...Meta
    }
    image {
      ...Image
    }
    hoverImage {
      ...Image
    }
    links {
      ...Link
    }
    mainLink {
      ...Link
    }
    price
    title
    badge
  }
`;

export const ProductListFragment = gql`
  fragment ProductList on ProductList {
    _meta {
      ...Meta
    }
    products {
      ...NavProductCard
    }
    title
  }
  ${NavProductCardFragment}
`;
export const NavDropdownFragment = gql`
  fragment NavDropdown on NavDropdown {
    _meta {
      ...Meta
    }
    description
    navigationPromo {
      align
      links {
        ...Link
      }
      title
      image {
        ...Image
      }
    }
    centerContent {
      ...LinkGroup
      ...LinkList
      ...ProductList
    }
    rightContent {
      links {
        ...Link
      }
      socialLinks {
        title
        links {
          ...LinkWithIcon
        }
      }
      search {
        searchCategory: searchBy
        placeholder: searchPlaceHolder
      }
    }
    title
  }
`;

export const SitewideBannerFragment = gql`
  fragment SitewideBanner on SitewideBanner {
    sitewidePromotions {
      countdownClock {
        startDate
        startTime
        endDate
        endTime
        endsInText
      }
      link {
        ...Link
      }
      text
      promotionId
      isDefault
      rotationTime
    }
    dealerLocatorLink {
      ...Link
    }
  }
`;

export const SecondaryNavFragment = gql`
  fragment SecondaryNav on SecondaryNav {
    _meta {
      schema
    }
    items {
      ...Link
      ...NavDropdown
    }
  }
`;

export const PageHeaderFragment = gql`
  fragment PageHeader on PageHeader {
    json
    searchLink {
      ...Link
    }
    sitewideBanner {
      ...SitewideBanner
    }
    account {
      greeting
      link {
        ...Link
      }
      loggedInLabel
      loggedOutLabel
    }
    bottomMobileLinks {
      ...Link
    }
    navItems {
      title
      titleAsLink {
        ...Link
      }
      _meta {
        ...Meta
      }
      content {
        ...NavDropdown
        ...SecondaryNav
      }
    }
    searchLink {
      ...Link
    }
  }
  ${SecondaryNavFragment}
  ${NavDropdownFragment}
  ${LinkGroupFragment}
  ${LinkListFragment}
  ${LinkWithIconFragment}
  ${SitewideBannerFragment}
  ${ProductListFragment}
`;

export const BaseShopFragment = gql`
  fragment BaseShop on Shop {
    id
    originalPrice: price
    currency
    inventory {
      orderable
      stock_level
    }
    step_quantity
    price: calculatedPrice
    productUrl
    ctaButtonType
    mainCategory
    mainImageUrl
    discount
    isGrill
    isExtendedWarranty
    promotionIds
    description
    weight
    color
    title
    upc
  }
`;

export const ShopFragment = gql`
  ${BaseShopFragment}
  fragment Shop on Shop {
    ...BaseShop
    customFields
    disableLocally
    promotionIds
    promotions {
      icon
      id
      title
    }
    variationAttributes {
      id
      name
      values {
        value
      }
    }
    variationValues {
      name
      value
    }
    variations {
      customFields
      colorVariationImage
      title
      productUrl
      mainImageUrl
      mainCategory
      description
      ctaButtonType
      originalPrice: price
      price: calculatedPrice
      discount
      id
      inventory {
        orderable
        stock_level
      }
      variationValues {
        name
        value
      }
    }
    warrantyPlanId
    options {
      id
      name
      values {
        default
        id
        name
        price
      }
    }
  }
`;

export const ProductFragment = gql`
  fragment Product on Product {
    _meta {
      ...Meta
    }
    addOns {
      label
      addons {
        sku
        overlayImage {
          ...Asset
        }
      }
    }
    alternateColorsV2 {
      colors {
        color
        link {
          ...Link
        }
        sku
      }
    }
    alternateSizes {
      sizes {
        ...Link
      }
    }
    bestFor
    color
    customizable
    compatibleWith {
      link {
        ...Link
      }
      image {
        _meta {
          schema
        }
        altTag
        static
        image {
          _meta {
            ...Meta
          }
          accessibilityLabel
          defaultHost
          id
          name
          endpoint
          url(h: 24, w: 24)
        }
      }
    }
    dark
    contentRows {
      json
    }
    description
    exclusiveDealers {
      image {
        ...Asset
      }
      link {
        ...Link
      }
      title
    }
    featuresData {
      features {
        description
        featureType
        title
        link {
          link
        }
      }
    }
    frontFacingImages {
      closedImage {
        image {
          ...Asset
        }
      }
      openImage {
        image {
          ...Asset
        }
      }
    }
    foodCapacity {
      capacities {
        foodType
        title
        image {
          defaultHost
          endpoint
          id
          name
        }
      }
    }
    images {
      ...Asset
    }
    lifestyleImages {
      ...Asset
    }
    maintenance {
      youtubeLink {
        contentUrl
      }
    }
    ownerManual {
      ownerManualLink {
        _meta {
          ...Meta
        }
        displayName
        externalLink
      }
    }
    productData {
      newArrivalStart
      newArrivalEnd
      categories {
        _meta {
          ...Meta
        }
        categories
      }
      cookingSpace {
        tier {
          width
          depth
        }
      }
      pairings
    }
    promoMarkdown
    prop65 {
      _meta {
        ...Meta
      }
      title
      warningBody
      warningName
      warningType
    }
    ratingSummary {
      id
      average
      count
      distribution {
        Count
        RatingValue
      }
    }
    seo {
      ...Seo
    }
    shortDescription
    shortTitle
    shopData(currency: $currency) {
      ...Shop
    }
    smallHeading
    spinSetUrl
    template
    transparentImages {
      ...Asset
    }
    sizes {
      _meta {
        ...Meta
      }
      shortTitle
      shopData(currency: $currency) {
        ...Shop
      }
      frontFacingImages {
        closedImage {
          image {
            ...Asset
          }
        }
      }
      productData {
        cookingSpace {
          tier {
            depth
            width
          }
        }
      }
    }
    title
  }
`;

export const RecipeFragment = gql`
  fragment Recipe on Recipe {
    id
    _meta {
      ...Meta
    }
    contentRows {
      json
    }
    recipeMetaSection {
      isFeatured
      title
      keywords
      relatedCategories
      description
      shortDescription
      cookTime
      prepTime
      complexity
      hardwood
      servingSizeMin
      servingSizeMax
    }
    beforeYouGetStarted {
      title
      summary
    }
    steps {
      cookCycleInfo {
        _meta {
          ...Meta
        }
        cookingLength
        cookingMethod
        grillTemp
        grillTemperature {
          celsius
          fahrenheit
        }
        inductionHob {
          hobPowerLevels
          hobTimer
        }
        keepWarm
        probeTarget
        probeTargetTemperature {
          celsius
          fahrenheit
        }
        superSmoke
      }
      ingredientGroup
      title
      instruction
      stepIndex
      Ingredients {
        unitOfMeasure
        imperial {
          measurement
          measurementDisplay
          unitOfMeasure
        }
        metric {
          measurement
          measurementDisplay
          unitOfMeasure
        }
        ingredient {
          name
          product {
            ...Product
          }
        }
      }
      proTip {
        tip
        link {
          ...Link
        }
      }
      assets {
        ...Asset
      }
      youtubeLink {
        name
        contentUrl
        thumbnailUrl
      }
    }
    grillCommand
    list {
      ...ShopableList
    }
    author {
      id
      name
    }
    assets {
      ...Asset
    }
    SEO {
      ...Seo
    }
    notes {
      id
      recipeId
      recipeIdAndPrivacy
      recipeIdAndUserId
      recipeIdAndUserIdAndStepIndex
      createdAt
      createdByUserId
      updatedAt
      upVotes
      downVotes
      stepIndex
      parentRecipeIdAndUserId
      comment
      privacy
    }
    ratingsAndReviews {
      totalReviews
      ratingSummary {
        average
        count
      }
    }
    youtubeLink {
      name
      contentUrl
      duration
      embedUrl
      thumbnailUrl
      uploadDate
      hostKey
    }
    savedByUser
    ingredientSummary {
      hasMetric
      hasImperial
      groups {
        groupKey
        groupName
        ingredients {
          imperial {
            ...Measure
          }
          metric {
            ...Measure
          }
          unitOfMeasure
          ingredient {
            name
            product {
              ...Product
            }
          }
        }
      }
    }
    saveCount
    servingSizes {
      people
    }
    recipeMigrationSection {
      slugId
    }
    shoppableTools {
      title
      products {
        ...Product
      }
    }
  }
`;

export const RecipeCategoryBodyFragment = gql`
  fragment RecipeCategoryBody on RecipeCategoryBody {
    _meta {
      ...Meta
    }
    category
    categoryFilters {
      _meta {
        ...Meta
      }
      filterType
      options {
        filter
        title
      }
      title
    }
    contentRows {
      json
    }
    recipeFilters {
      name
      options
    }
    json
  }
`;

export const IngredientFragment = gql`
  ${MeasureFragment}

  fragment Ingredient on RecipeIngredients {
    imperial {
      ...Measure
    }
    metric {
      ...Measure
    }
    unitOfMeasure
    ingredient {
      name
    }
  }
`;

export const OptionItemFragment = gql`
  fragment OptionItem on LineItem {
    image
    title: name
    optionId
    optionValueId
    originalPrice
    price
    quantity
    sku
  }
`;

export const BaseLineItemFragment = gql`
  ${OptionItemFragment}
  fragment BaseLineItem on LineItem {
    baseDiscount
    basePrice
    bonus
    category
    description
    discount
    id
    image
    title: name
    originalPrice
    price
    productUrl
    quantity
    sku
    tax
    optionItems {
      ...OptionItem
    }
    gift
    giftMessage
    options {
      id
      name
      values {
        id
        price
        name
      }
    }
  }
`;

export const BasketLineItemFragment = gql`
  ${BaseLineItemFragment}
  fragment BasketLineItem on LineItem {
    ...BaseLineItem
    warrantyPlanId
    options {
      id
      name
      values {
        id
        price
        name
      }
    }
  }
`;

export const GiftCardFragment = gql`
  fragment GiftCard on GiftCard {
    id
    amount
    message
    email
    toName
    fromName
  }
`;

export const RecommendationFragment = gql`
  fragment Recommendation on Recommendation {
    link
    sku
    title
    originalPrice: price
    price: salePrice
    mainImageUrl
    productType
    recToken
    category
    description
    ratingSummary {
      id
      average
      count
      distribution {
        Count
        RatingValue
      }
    }
  }
`;

export const BasketFragment = gql`
  ${BasketLineItemFragment}
  ${GiftCardFragment}

  fragment Basket on Basket {
    outOfStockItems {
      sku
    }
    id
    basketJWT
    currency
    customerId
    totalItems
    giftCards {
      ...GiftCard
    }
    lineItems {
      ...BasketLineItem
    }
    #    recommendations {
    #      ...Recommendation
    #    }
    giftCardTotal
    freeProductTotal
    subtotal
    shippingTotal
    total
    tax
    shopToken
  }
`;

export const OrderFragment = gql`
  ${BaseLineItemFragment}
  ${GiftCardFragment}

  fragment Order on Order {
    details {
      basketId
      billingAddress {
        address1
        address2
        city
        fullName
        phone
        postalCode
        stateCode
        countryCode
      }
      couponItems {
        code
      }
      creationDate
      currency
      customerInfo {
        email
        customerNo
      }
      firstName
      giftCertificateItems {
        ...GiftCard
      }
      lastName
      orderNo
      orderPriceAdjustments {
        appliedDiscount {
          amount
          percentage
          type
        }
        couponCode
        itemText
        price
      }
      orderTotal
      packages {
        trackingUrl
        items {
          sku
          quantity
        }
        status
      }
      paymentInstruments {
        paymentCard {
          cardType
          expirationMonth
          expirationYear
          numberLastDigits
        }
      }
      productItems {
        ...BaseLineItem
      }
      shipments {
        shippingAddress {
          address1
          address2
          city
          countryCode
          fullName
          phone
          postalCode
          stateCode
        }
        shippingMethod {
          description
          name
        }
      }
      shippingTotal
      status
      subTotal
      taxTotal
    }
  }
`;

export const LearnFragment = gql`
  fragment Learn on LearningContent {
    _meta {
      ...Meta
    }
    articleCollection {
      navLinks {
        ...Link
      }
      title
    }
    articleModules {
      headerText
      image {
        ...Asset
      }
      markdownText
      pullQuote
      youtubeVideo {
        ...Video
      }
    }
    articleType
    components
    featuredContent
    headerAsset {
      image {
        ...Asset
      }
      youtubeLink {
        ...Video
      }
    }
    id
    isPremiumContent
    locale
    navigation
    publicationDate
    SEO: seo {
      ...Seo
    }
    tags {
      tags
    }
    title
    type {
      categories
    }
    recipe {
      _meta {
        ...Meta
      }
      ingredientSummary {
        groups {
          ingredients {
            ingredient {
              name
            }
          }
        }
      }
      id
      author {
        name
        avatar {
          url
        }
      }
      assets {
        url
      }
      recipeMetaSection {
        title
        description
        cookTime
        prepTime
        hardwood
        servingSizeMin
      }
      ratingSummary {
        average
        count
      }
      steps {
        cookCycleInfo {
          _meta {
            ...Meta
          }
          cookingLength
          cookingMethod
          grillTemp
          grillTemperature {
            celsius
            fahrenheit
          }
          inductionHob {
            hobPowerLevels
            hobTimer
          }
          keepWarm
          probeTarget
          probeTargetTemperature {
            celsius
            fahrenheit
          }
          superSmoke
        }
        ingredientGroup
        title
        instruction
        stepIndex
      }
      ingredientSummary {
        hasMetric
        hasImperial
        groups {
          groupKey
          groupName
          ingredients {
            imperial {
              ...Measure
            }
            metric {
              ...Measure
            }
            unitOfMeasure
            ingredient {
              name
            }
          }
        }
      }
    }
  }
`;
